/**
 * C-20 接続（アプリ利用者）＝**T-D24／D-67**。
 *
 * **なぜ要るか**：招待コードの失効（C-13）も得意先の利用停止（C-09）も、
 * **すでにつながっている職人のアプリ発注を止めない**。両画面はそれを正しく書いているのに、
 * 「取引先ごとの接続を解除してください」の**行き先が無かった**（監査 flow#3）。ここがその行き先。
 *
 * 認可（03 §7 の接続行）：**参照は staff も可・付け替えと解除は admin のみ**。
 * 押しても 403 になるボタンは出さない（console の一貫した作法）。
 *
 * ★**解除しても、相手が手元の招待コードを入れ直せば戻れる**（`create_connection` の ON CONFLICT）。
 *   確認UIで隠さずに書き、招待コードの失効へ導く。書かないと「解除したのに注文が来た」になる。
 */

import { auth, console_, type Connection, type Customer } from "@kenzai/api-client";
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  ErrorNote,
  formatDateTime,
  PageHeader,
  Spinner,
  Table,
  Td,
} from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  hintStyle,
  inputStyle,
  labelStyle,
  overlayStyle,
  warnBanner,
} from "./formStyles";
import { LineAlertChip } from "./orderDisplay";

/** backend の `CONNECTION_LIST_LIMIT` と同値。**黙って切らない**ために画面でも知っておく。 */
const LIST_LIMIT = 500;

/** 職人をどう名乗らせるか。**無記名の行を作らない**（D-67③）＝最後は ID で必ず埋める。 */
function builderLabel(c: Connection): string {
  return c.person_name ?? c.display_name ?? `職人 #${c.builder_user_id}`;
}

/** 呼びかけ。氏名も表示名も無いときに「#12 さん」とは書かない（人でなく番号なので）。 */
function builderAddress(c: Connection): string {
  return c.person_name ?? c.display_name ?? `この接続（職人 #${c.builder_user_id}）`;
}

/**
 * LINE 側の状態＝**通知が届くかどうか**。接続の有効/解除とは別物なので分けて出す。
 * `unlinked`（友だち追加していない）も `blocked`（ブロックした）も、通知は1通も届かない。
 *
 * ★**M1.6（FR-130・D-89）で「LINE未達」チップ様式に統一した**＝状態名だけを出していた頃は、
 *   「LINE ブロック中」と読めても「だからこの人には通知が1通も届いていない」まで辿れなかった。
 *   受注管理・受注詳細と**同じチップ**を出し、行の脇に「何が起きていて、次に何をするか」を書く。
 * ★理由ごとに打つ手が違う（ブロック＝電話／未追加＝追加のご案内）ので、`what` は分けて持つ。
 */
const LINE_WARNING: Record<string, { why: string; what: string }> = {
  unlinked: {
    why: "LINE の友だち追加がまだです",
    what: "招待コード・QR をもう一度お渡しし、友だち追加をご案内ください。",
  },
  blocked: {
    why: "LINE をブロックされています",
    what: "お電話等でご連絡ください。",
  },
};

/**
 * C-20 接続（アプリ利用者）。**D-79 で取引先（C-09）配下のタブになった**。
 *
 * `customerId` を渡すと**その取引先だけ**の埋め込み表示（見出しは親が出す）。
 * ★絞り込みは**サーバ側**（`customer_id` パラメータ）＝以前はクライアントで全件から
 *   filter していたため、一覧の上限 500 件を超えると黙って取りこぼしていた。
 */
export function Connections({ customerId: fixedCustomerId }: { customerId?: number } = {}) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const embedded = fixedCustomerId != null;
  const customerFilter = embedded ? String(fixedCustomerId) : params.get("customer_id");
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [reassigning, setReassigning] = useState<Connection | null>(null);
  const [revoking, setRevoking] = useState<Connection | null>(null);

  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";

  const list = useQuery({
    queryKey: ["connections", includeRevoked, customerFilter ?? ""],
    queryFn: () => console_.connections(includeRevoked, customerFilter ? Number(customerFilter) : undefined),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["connections"] });

  /** 失敗したときは**モーダルを閉じてから**知らせる＝ErrorNote は本文側に描くので、
   *  開いたままだと覆い（z-index）の裏に隠れて「押しても何も起きない」に見える。
   *  ここで起きる失敗（422 解除済み・409 版ずれ）はどれも「画面が古い」なので、
   *  閉じて最新を取り直すのが正しい次の一手でもある。 */
  const revoke = useMutation({
    mutationFn: ({ row, reason }: { row: Connection; reason: string }) =>
      console_.revokeConnection(row.id, reason),
    onSettled: () => {
      setRevoking(null);
      invalidate();
    },
  });

  const reassign = useMutation({
    mutationFn: ({ row, customerId }: { row: Connection; customerId: number }) => {
      // ★楽観ロックの基準は**発火時に生きているクエリから読む**（OrderDetail と同じ作法）。
      //   モーダルを開いた時点の値を握り続けると、間に一覧が更新されただけで 409 になる。
      const fresh = (list.data ?? []).find((c) => c.id === row.id) ?? row;
      return console_.reassignConnection(row.id, customerId, fresh.updated_at);
    },
    onSuccess: () => {
      setReassigning(null);
      invalidate();
    },
    // 失敗時はモーダルを開いたままにして、**その中に**エラーを出す（選び直せる）
    onError: () => invalidate(),
  });

  if (list.isLoading || me.isLoading) return <Spinner />;
  if (list.isError) return <ErrorNote error={list.error} />;

  const all = list.data ?? [];
  const rows = all; // 絞り込みはサーバ側（D-79）＝クライアント filter は上限で取りこぼす
  const filterName = customerFilter
    ? // 接続が0件だと名前を引けない。**内部IDを画面に出さない**（利用者に意味が無く、
      // 番号を見せる必要も無い）＝分からないときは「この取引先」と言う
      (all.find((c) => c.customer_id === Number(customerFilter))?.customer_name ?? "この取引先")
    : null;

  return (
    <div>
      {!embedded && (
        <PageHeader
          title="接続（アプリ利用者）"
          subtitle="招待コードでつながり、アプリから注文できる方の一覧です。"
        />
      )}

      <p style={{ ...hintStyle, marginBottom: "var(--kz-space-4)" }}>
        {/* 03 §3：コードの失効と接続の失効は独立。ここを書かないと「止めたのに注文が来る」になる */}
        招待コードを失効させても、すでにつながっている方の注文は止まりません。取引を終えるときは、この画面で接続を解除します。
        {!isAdmin && "（解除と取引先の変更は管理者のみ行えます）"}
      </p>

      {/* 解除の失敗はここに出る（モーダルは閉じてから出す＝覆いの裏に隠れない）。
          付け替えの失敗はモーダルの**中**に出す（選び直せるので閉じない） */}
      {revoke.isError && <ErrorNote error={revoke.error} />}

      {rows.length >= LIST_LIMIT && (
        // 黙って切らない（この一覧はページングを持たない）
        <p style={warnBanner} data-testid="connections-truncated">
          接続が多いため、新しい順に {LIST_LIMIT} 件までを表示しています。
        </p>
      )}

      <Card style={{ marginBottom: "var(--kz-space-4)" }} padding="var(--kz-space-3)">
        <div
          style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-4)", flexWrap: "wrap" }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--kz-space-2)",
              fontSize: "var(--kz-font-size-sm)",
            }}
          >
            <input
              type="checkbox"
              checked={includeRevoked}
              data-testid="include-revoked"
              onChange={(e) => setIncludeRevoked(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            解除済みも表示する
          </label>
          {/* 05 §6.1／T-D24 の「得意先別」＝C-09 の単票から絞り込んで飛んで来られる。
              ★**埋め込み（取引先単票のタブ）では出さない**＝その画面は最初からその取引先の
              中なので、「絞り込みを外す」を押しても何も起きない（レビュー確定）。 */}
          {!embedded && filterName && (
            <span style={{ fontSize: "var(--kz-font-size-sm)" }} data-testid="customer-filter">
              取引先「{filterName}」で絞り込み中{" "}
              <button
                type="button"
                className="kz-btn"
                data-testid="clear-customer-filter"
                onClick={() => {
                  params.delete("customer_id");
                  setParams(params, { replace: true });
                }}
              >
                絞り込みを外す
              </button>
            </span>
          )}
        </div>
      </Card>

      <Card padding="0">
        {rows.length === 0 ? (
          <Empty
            title={
              filterName ? `「${filterName}」につながっている方はいません。` : "つながっている方はいません。"
            }
            hint="「招待コード」で発行したコードを取引先にお渡しし、アプリで入力してもらうとここに並びます。"
          />
        ) : (
          <Table head={["ご担当者", "取引先", "状態", "つながった日", ""]}>
            {rows.map((c) => (
              <tr key={c.id} data-testid={`connection-${c.id}`}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{builderLabel(c)}</div>
                  <div style={hintStyle}>
                    {c.person_name
                      ? // 本人が登録した氏名を出している＝LINE 表示名は補助情報
                        (c.display_name ?? "LINE の表示名は未取得です")
                      : c.display_name
                        ? "お名前は未登録です（LINE の表示名を出しています）"
                        : // 表示名も無い＝出せる名前が1つも無い（番号で呼んでいる）
                          "お名前も LINE の表示名も未登録です"}
                  </div>
                </Td>
                <Td>
                  {c.customer_id ? (
                    <Link
                      to={`/masters/customers/${c.customer_id}`}
                      style={{ color: "var(--kz-color-primary)" }}
                    >
                      {c.customer_name ?? `#${c.customer_id}`}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td>
                  <span data-testid={`connection-status-${c.id}`}>
                    {c.status === "active" ? (
                      <Badge tone="ok">有効</Badge>
                    ) : (
                      <Badge tone="neutral">解除済み</Badge>
                    )}
                  </span>{" "}
                  {/* 接続は生きているが LINE 側で通知が届かない状態。画面が「有効」としか
                      言わないと、卸は「なぜ通知が届かないのか」を追えない（D-67③）。
                      **既定は unlinked**（友だち追加前）なので blocked だけ見ると取りこぼす */}
                  {c.status === "active" && c.builder_status && LINE_WARNING[c.builder_status] && (
                    <>
                      <LineAlertChip
                        testId={`connection-line-${c.id}`}
                        title={`${LINE_WARNING[c.builder_status].why}。${LINE_WARNING[c.builder_status].what}`}
                      />
                      <div style={hintStyle}>
                        {LINE_WARNING[c.builder_status].why}。{LINE_WARNING[c.builder_status].what}
                      </div>
                    </>
                  )}
                </Td>
                <Td style={{ whiteSpace: "nowrap" }}>
                  <span className="kz-num">{formatDateTime(c.connected_at)}</span>
                  {c.revoked_at && (
                    // 「解除済みも表示する」の主目的＝いつ切ったか
                    <div style={hintStyle}>
                      解除 <span className="kz-num">{formatDateTime(c.revoked_at)}</span>
                    </div>
                  )}
                </Td>
                <Td>
                  {isAdmin && c.status === "active" && (
                    <>
                      <button
                        type="button"
                        className="kz-btn kz-btn--sm"
                        data-testid={`connection-reassign-${c.id}`}
                        onClick={() => {
                          reassign.reset();
                          setReassigning(c);
                        }}
                      >
                        取引先を変更
                      </button>{" "}
                      <button
                        type="button"
                        className="kz-btn"
                        data-testid={`connection-revoke-${c.id}`}
                        onClick={() => {
                          revoke.reset();
                          setRevoking(c);
                        }}
                      >
                        接続を解除
                      </button>
                    </>
                  )}
                  {c.status === "revoked" && (
                    // ★戻す API は無い（D-18 招待制）＝**次の一手**をその場に置く
                    <span style={hintStyle}>
                      もう一度つなぐには{" "}
                      <Link
                        to={c.customer_id ? `/invite-codes?customer_id=${c.customer_id}` : "/invite-codes"}
                        style={{ color: "var(--kz-color-primary)" }}
                      >
                        招待コード
                      </Link>{" "}
                      をお渡しします
                    </span>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {reassigning && (
        <ReassignDialog
          row={reassigning}
          error={reassign.error}
          busy={reassign.isPending}
          onCancel={() => setReassigning(null)}
          onSubmit={(customerId) => reassign.mutate({ row: reassigning, customerId })}
        />
      )}

      {revoking && (
        <ConfirmDialog
          title={`${builderAddress(revoking)}の接続を解除しますか？`}
          body={
            <>
              この方はアプリから注文できなくなります（<strong>すぐに反映されます</strong>）。
              これまでのご注文の記録は残ります。
              <br />
              {/* ★ここを書かないと「解除したのに注文が来た」になる＝相手は手元のコードで戻れる */}
              <strong>
                お渡し済みの招待コードが有効なままだと、相手はそれを入力して再びつながれます。
              </strong>
              取引を完全に終える場合は、あとで「招待コード」の画面でそのコードも失効させてください。
              <br />
              理由は記録に残ります（電話番号・メールアドレスは伏字で保存されます）。
            </>
          }
          confirmLabel="接続を解除する"
          tone="danger"
          requireReason
          busy={revoke.isPending}
          onCancel={() => setRevoking(null)}
          onConfirm={(reason) => revoke.mutate({ row: revoking, reason })}
        />
      )}
    </div>
  );
}

/** 付け替え＝**入力があるモーダル**なので背景クリックでは閉じない（打ち直しを起こさない）。
 *  取引先は**サーバ側で検索**する＝一覧APIは100件で頭打ちなので、この中で絞れないと
 *  101件目以降の取引先へは永久に付け替えられない。 */
function ReassignDialog({
  row,
  error,
  busy,
  onCancel,
  onSubmit,
}: {
  row: Connection;
  error: unknown;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (customerId: number) => void;
}) {
  const [q, setQ] = useState("");
  const [value, setValue] = useState("");
  const customers = useQuery({
    queryKey: ["customers", q],
    queryFn: () => console_.customers(`?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  });

  // 現在の帰属先は選ばせない（選んでも何も変わらないのに監査ログだけ増える）
  const choices = (customers.data?.items ?? []).filter((c: Customer) => c.id !== row.customer_id);
  const hasMore = customers.data?.next_cursor != null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="取引先を変更">
      <Card>
        <h2 style={{ marginTop: 0 }}>取引先を変更</h2>
        <p style={hintStyle}>
          {builderLabel(row)} さんの帰属先を変えます。<strong>これからのご注文</strong>
          の掛率・単価・現場の候補が新しい取引先のものになります（これまでのご注文は変わりません）。
        </p>
        {/* 失敗はモーダルの**中**に出す＝本文側に出すと覆いの裏に隠れて見えない */}
        <ErrorNote error={error} />
        <label style={labelStyle}>
          取引先を探す
          <input
            style={{ ...inputStyle, width: "100%" }}
            value={q}
            placeholder="名前・番号の一部"
            data-testid="reassign-search"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <label style={labelStyle}>
          変更後の取引先
          <select className="kz-select"
            style={{ ...inputStyle, width: "100%" }}
            value={value}
            data-testid="reassign-customer"
            disabled={customers.isLoading}
            onChange={(e) => setValue(e.target.value)}
          >
            <option value="">
              {customers.isLoading
                ? "読み込んでいます…"
                : choices.length === 0
                  ? "該当する取引先がありません"
                  : "選んでください"}
            </option>
            {choices.map((c: Customer) => (
              <option key={c.id} value={c.id}>
                {c.display_no ? `${c.display_no} ` : ""}
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {customers.isError && <ErrorNote error={customers.error} />}
        {hasMore && (
          <p style={hintStyle}>候補が多いため先頭 100 件を出しています。上の検索で絞り込んでください。</p>
        )}
        <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-4)" }}>
          <button
            type="button"
            className="kz-btn kz-btn--primary"
            disabled={busy || !value}
            data-testid="reassign-submit"
            onClick={() => onSubmit(Number(value))}
          >
            {busy ? "変更しています…" : "取引先を変更する"}
          </button>
          <button type="button" className="kz-btn" onClick={onCancel}>
            やめる
          </button>
        </div>
      </Card>
    </div>
  );
}
